import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import AvailabilityEditor from "../../components/AvailabilityEditor";
import {
  LogOut, Plus, Trash2, Pencil, Loader2, Search, KeyRound, Users, Calendar,
  Mail, UserPlus, LayoutDashboard, ChevronRight, CheckCircle2, Archive, ExternalLink, RefreshCw, Film,
  Upload, X, Image as ImageIcon, Phone, MessageCircle, Instagram, MapPin, Ruler, ChevronLeft, ZoomIn, Copy, Hash,
  CalendarCheck, Briefcase, Building2, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken, setAdminToken, API } from "../../lib/api";
import { uploadImage } from "../../lib/uploadImage";
import OutreachDialog, { fmtDateTime } from "./OutreachDialog";

const SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "models", label: "Talents", icon: Users },
  { key: "bookings", label: "Bookings", icon: CalendarCheck },
  { key: "applications", label: "Applications", icon: UserPlus },
  { key: "contacts", label: "Messages", icon: Mail },
];

const STATUS_COLOR = {
  new: "bg-amber-100 text-amber-900 border-amber-300",
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  contacted: "bg-blue-100 text-blue-900 border-blue-300",
  invited: "bg-indigo-100 text-indigo-900 border-indigo-300",
  confirmed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-zinc-100 text-zinc-500 border-zinc-300",
  signed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  rejected: "bg-zinc-100 text-zinc-700 border-zinc-300",
  archived: "bg-zinc-100 text-zinc-500 border-zinc-300",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [section, setSection] = useState("overview");
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);
  const [models, setModels] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [apps, setApps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login"); return; }
    api.get("/admin/me").then((r) => setAdmin(r.data)).catch(() => { setAdminToken(null); nav("/admin/login"); });
    refreshAll();
  }, []); // eslint-disable-line

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [s, a, m, c, ap, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/activity"),
        api.get("/models"),
        api.get("/admin/contacts"),
        api.get("/admin/applications"),
        api.get("/admin/bookings"),
      ]);
      setStats(s.data); setActivity(a.data); setModels(m.data);
      setContacts(c.data); setApps(ap.data);
      setBookings(b.data);
    } finally { setLoading(false); }
  };

  const logout = () => { setAdminToken(null); nav("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A] flex">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-black/10 bg-white sticky top-0 h-screen">
        <div className="px-6 pt-6 pb-8 border-b border-black/10">
          <Link to="/" className="font-serif text-xl">Ege Models<span className="text-zinc-500">.</span></Link>
          <p className="label-overline text-zinc-500 mt-2">Studio Admin</p>
        </div>
        <nav className="flex-1 py-6 px-4 space-y-1">
          {SECTIONS.map(({ key, label, icon: Icon }) => {
            const count = key === "models" ? stats.models
              : key === "applications" ? stats.applications_pending
              : key === "bookings" ? stats.bookings_new
              : key === "contacts" ? stats.contacts_new : null;
            return (
              <button
                key={key}
                data-testid={`nav-${key}`}
                onClick={() => setSection(key)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  section === key ? "bg-black text-white" : "hover:bg-black/5 text-zinc-700"
                }`}
              >
                <Icon size={16}/>
                <span className="label-overline tracking-wider">{label}</span>
                {count > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 ${
                    section === key ? "bg-white text-black" : "bg-amber-100 text-amber-900"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-black/10 p-4 space-y-2">
          <Link
            to="/admin/hero-video"
            data-testid="nav-hero-video"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-black/5"
          >
            <Film size={14}/> Hero video
          </Link>
          <Link
            to="/admin/latest"
            data-testid="nav-latest"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-black/5"
          >
            <ImageIcon size={14}/> The Latest
          </Link>
          <Link
            to="/admin/castings"
            data-testid="nav-castings"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-black/5"
          >
            <Megaphone size={14}/> Now Casting
          </Link>
          <Link
            to="/admin/instagram"
            data-testid="nav-instagram"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-black/5"
          >
            <Instagram size={14}/> Instagram posts
          </Link>
          <Link
            to="/admin/partners"
            data-testid="nav-partners"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-black/5"
          >
            <Building2 size={14}/> Partner agencies
          </Link>
          <PasswordDialog>
            <button data-testid="open-password-dialog" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-black/5">
              <KeyRound size={14}/> Change password
            </button>
          </PasswordDialog>
          <p className="text-xs text-zinc-500 px-3 truncate">{admin?.email || "—"}</p>
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-black/5">
            <LogOut size={14}/> Sign out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden border-b border-black/10 sticky top-0 z-10 bg-[#FFFFFF]/95 backdrop-blur">
          <div className="px-4 py-4 flex items-center justify-between">
            <Link to="/" className="font-serif text-lg">Ege Models</Link>
            <button onClick={logout} className="label-overline text-zinc-700"><LogOut size={14}/></button>
          </div>
          <div className="overflow-x-auto px-2 pb-2 flex gap-1">
            {SECTIONS.map(({ key, label }) => (
              <button key={key} onClick={() => setSection(key)} className={`label-overline whitespace-nowrap px-3 py-2 ${section === key ? "bg-black text-white" : "text-zinc-600"}`}>
                {label}
              </button>
            ))}
          </div>
        </header>

        <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-[1400px]">
          {loading && (
            <div className="mb-6 inline-flex items-center gap-2 text-zinc-500 label-overline">
              <Loader2 size={14} className="animate-spin"/> Loading…
            </div>
          )}

          {section === "overview" && <Overview stats={stats} activity={activity} setSection={setSection} onRefresh={refreshAll}/>}
          {section === "models" && <ModelsPanel models={models} onRefresh={refreshAll}/>}
          {section === "bookings" && <BookingsPanel items={bookings} onRefresh={refreshAll}/>}
          {section === "applications" && <ApplicationsPanel items={apps} onRefresh={refreshAll}/>}
          {section === "contacts" && <ContactsPanel items={contacts} onRefresh={refreshAll}/>}
        </div>
      </main>
    </div>
  );
}

/* ============== OVERVIEW ============== */
function Overview({ stats, activity, setSection, onRefresh }) {
  return (
    <>
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="label-overline text-zinc-500">Studio</p>
          <h1 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light mt-2">Overview.</h1>
        </div>
        <button onClick={onRefresh} data-testid="refresh-btn" className="btn-ghost"><RefreshCw size={14}/> Refresh</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
        <StatCard n={stats.models ?? 0} label="Talents" sub="On roster" onClick={() => setSection("models")}/>
        <StatCard n={stats.bookings ?? 0} label="Bookings" sub={`${stats.bookings_new ?? 0} new`} highlight={stats.bookings_new > 0} onClick={() => setSection("bookings")}/>
        <StatCard n={stats.applications ?? 0} label="Applications" sub={`${stats.applications_pending ?? 0} pending`} highlight={stats.applications_pending > 0} onClick={() => setSection("applications")}/>
        <StatCard n={stats.contacts ?? 0} label="Messages" sub={`${stats.contacts_new ?? 0} unread`} highlight={stats.contacts_new > 0} onClick={() => setSection("contacts")}/>
      </div>

      {/* Activity */}
      <div className="mt-12">
        <p className="label-overline text-zinc-500">Recent Activity</p>
        <h2 className="font-serif text-3xl mt-2 mb-6">What's happening.</h2>
        <div className="border border-black/10 bg-white">
          {activity.length === 0 ? (
            <p className="p-12 text-center text-zinc-500 label-overline">No activity yet — submissions will appear here.</p>
          ) : (
            <ul className="divide-y divide-black/5">
              {activity.map((it) => (
                <li key={`${it.type}-${it.id}`} className="px-6 py-4 flex items-center gap-4 hover:bg-black/[0.02] transition-colors">
                  <div className="w-24 shrink-0">
                    <Badge variant="outline" className="rounded-none border-black/15 text-xs uppercase tracking-wider font-normal">{it.type}</Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight truncate">{it.title}</p>
                    <p className="text-sm text-zinc-500 truncate">{it.subtitle}</p>
                  </div>
                  <div className="hidden md:flex items-center gap-3">
                    <StatusBadge status={it.status}/>
                    <span className="text-xs text-zinc-500 w-24 text-right">{relTime(it.created_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function StatCard({ n, label, sub, highlight, onClick }) {
  return (
    <button onClick={onClick} className={`text-left p-8 transition-colors ${highlight ? "bg-amber-50 hover:bg-amber-100" : "bg-white hover:bg-black/[0.02]"}`}>
      <p className="font-serif text-5xl lg:text-6xl tracking-tighter font-light">{n}</p>
      <p className="label-overline text-zinc-500 mt-3">{label}</p>
      <p className={`text-xs mt-1 ${highlight ? "text-amber-700" : "text-zinc-500"}`}>{sub}</p>
    </button>
  );
}

function StatusBadge({ status }) {
  if (!status) return null;
  const cls = STATUS_COLOR[status] || "bg-zinc-100 border-zinc-300 text-zinc-700";
  return <span className={`text-xs px-2 py-0.5 border ${cls}`}>{status}</span>;
}

/* ============== MODELS ============== */
function ModelsPanel({ models, onRefresh }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => models.filter((m) => {
    if (filter !== "all" && m.gender !== filter) return false;
    if (q && !m.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [models, q, filter]);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="label-overline text-zinc-500">Roster</p>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-2">Talents.</h1>
          <p className="text-zinc-500 mt-1 text-sm">{filtered.length} of {models.length}</p>
        </div>
        <ModelDialog onSaved={onRefresh}>
          <button data-testid="add-model-btn" className="btn-primary"><Plus size={14}/> New Talent</button>
        </ModelDialog>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar value={q} onChange={setQ} placeholder="Search by name…"/>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 bg-white border-black/15 rounded-none"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-white border-black/15 rounded-none">
            <SelectItem value="all">All divisions</SelectItem>
            <SelectItem value="women">Women</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyCard>No talents yet — add your first model to start the roster.</EmptyCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <div key={m.id} className="border border-black/10 bg-white group">
              <div className="aspect-[3/4] bg-zinc-200 relative overflow-hidden">
                <img src={m.cover_image} alt={m.name} className="w-full h-full object-cover"/>
                {m.featured && (
                  <span className="absolute top-2 left-2 label-overline bg-black text-white px-2 py-1">Featured</span>
                )}
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-serif text-lg truncate">{m.name}</p>
                    <p className="text-xs text-zinc-500">{m.gender} · {m.location || "—"}</p>
                  </div>
                  <Link to={`/models/${m.slug}`} target="_blank" className="text-zinc-500 hover:text-black p-1" title="View public page">
                    <ExternalLink size={14}/>
                  </Link>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(m.categories || []).slice(0, 3).map((c) => (
                    <span key={c} className="text-[10px] uppercase tracking-wider border border-black/15 px-1.5 py-0.5">{c}</span>
                  ))}
                </div>
                <div className="flex gap-2 pt-2 border-t border-black/5">
                  <ModelDialog model={m} onSaved={onRefresh}>
                    <button data-testid={`edit-model-${m.id}`} className="flex-1 label-overline text-xs text-zinc-700 hover:text-black py-1 inline-flex items-center justify-center gap-1">
                      <Pencil size={12}/> Edit
                    </button>
                  </ModelDialog>
                  <button data-testid={`delete-model-${m.id}`} onClick={async () => {
                    if (!window.confirm(`Delete ${m.name}?`)) return;
                    await api.delete(`/admin/models/${m.id}`);
                    toast.success("Deleted");
                    onRefresh();
                  }} className="flex-1 label-overline text-xs text-red-700 hover:text-red-900 py-1 inline-flex items-center justify-center gap-1">
                    <Trash2 size={12}/> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ============== BOOKINGS ============== */
function BookingsPanel({ items, onRefresh }) {
  const [filter, setFilter] = useState("all"); // all | new | contacted | confirmed | declined | completed
  const [q, setQ] = useState("");

  const filtered = useMemo(() => items.filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = `${b.client_name} ${b.company || ""} ${b.email} ${b.model_name || ""} ${b.reference}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }), [items, filter, q]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete booking from ${name}?`)) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="label-overline text-zinc-500">Inbox</p>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-2">Bookings.</h1>
          <p className="text-zinc-500 mt-1 text-sm">{filtered.length} of {items.length}</p>
        </div>
        <button onClick={onRefresh} className="btn-ghost"><RefreshCw size={14}/> Refresh</button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar value={q} onChange={setQ} placeholder="Search by client, model, ref…"/>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-white border-black/15 rounded-none"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-white border-black/15 rounded-none">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyCard>No booking inquiries yet — they'll appear here when clients submit the form on a model profile.</EmptyCard>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <BookingCard key={b.id} booking={b} onStatus={setStatus} onDelete={remove}/>
          ))}
        </div>
      )}
    </>
  );
}

function BookingCard({ booking: b, onStatus, onDelete }) {
  const [open, setOpen] = useState(false);
  const created = b.created_at ? new Date(b.created_at).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "";
  const statusClass = STATUS_COLOR[b.status] || STATUS_COLOR.new;

  return (
    <div data-testid={`booking-card-${b.id}`} className="border border-black/10 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-5 grid grid-cols-12 gap-4 items-center hover:bg-zinc-50 transition-colors"
      >
        <div className="col-span-12 md:col-span-5">
          <p className="font-serif text-xl leading-tight">{b.client_name}</p>
          <p className="text-xs text-zinc-500 mt-1 flex items-center flex-wrap gap-x-3 gap-y-1">
            {b.company && <span className="inline-flex items-center gap-1"><Building2 size={11}/> {b.company}</span>}
            <span>{b.email}</span>
            {b.phone && <span>{b.phone}</span>}
          </p>
        </div>
        <div className="col-span-6 md:col-span-3">
          <p className="label-overline text-zinc-500">Talent</p>
          <p className="text-sm mt-1 truncate">{b.model_name || "—"}</p>
        </div>
        <div className="col-span-6 md:col-span-2">
          <p className="label-overline text-zinc-500">Project</p>
          <p className="text-sm mt-1 capitalize truncate">{b.project_type || "—"}</p>
        </div>
        <div className="col-span-12 md:col-span-2 flex items-center md:justify-end gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${statusClass}`}>{b.status}</span>
          <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`}/>
        </div>
        <div className="col-span-12 text-[11px] text-zinc-400 font-mono">
          {b.reference} · {created}
        </div>
      </button>

      {open && (
        <div className="border-t border-black/10 px-5 pb-5 pt-4 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div className="md:col-span-2 space-y-4">
            <div>
              <p className="label-overline text-zinc-500">Brief</p>
              <p className="mt-2 text-zinc-800 whitespace-pre-wrap leading-relaxed">{b.message}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Meta label="Shoot dates" value={b.shoot_dates}/>
              <Meta label="Location" value={b.location}/>
              <Meta label="Budget" value={b.budget_range}/>
              <Meta label="Project" value={b.project_type ? b.project_type[0].toUpperCase() + b.project_type.slice(1) : null}/>
            </div>
          </div>
          <div className="space-y-3">
            <a href={`mailto:${b.email}?subject=Re%3A ${encodeURIComponent(b.model_name || "Booking")} %E2%80%94 ${encodeURIComponent(b.reference)}`}
               className="btn-primary w-full justify-center inline-flex items-center gap-2"
               data-testid={`booking-reply-${b.id}`}>
              <Mail size={14}/> Reply by email
            </a>
            {b.phone && (
              <a href={`https://wa.me/${b.phone.replace(/\D/g, "")}`}
                 target="_blank" rel="noreferrer"
                 className="btn-ghost w-full justify-center inline-flex items-center gap-2">
                <MessageCircle size={14}/> WhatsApp
              </a>
            )}
            {b.model_slug && (
              <Link to={`/models/${b.model_slug}`} target="_blank"
                    className="btn-ghost w-full justify-center inline-flex items-center gap-2">
                <ExternalLink size={14}/> Open talent profile
              </Link>
            )}

            <div className="pt-3 border-t border-black/10">
              <p className="label-overline text-zinc-500 mb-2">Move to</p>
              <div className="grid grid-cols-2 gap-2">
                {["contacted","confirmed","completed","declined"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onStatus(b.id, s)}
                    disabled={b.status === s}
                    data-testid={`booking-status-${s}-${b.id}`}
                    className={`text-xs uppercase tracking-wider border px-2 py-2 transition-colors ${
                      b.status === s
                        ? "bg-black text-white border-black cursor-default"
                        : "bg-white text-zinc-700 border-black/15 hover:border-black hover:text-black"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onDelete(b.id, b.client_name)}
                data-testid={`booking-delete-${b.id}`}
                className="mt-3 w-full text-xs text-red-700 hover:text-red-900 inline-flex items-center justify-center gap-1 py-2"
              >
                <Trash2 size={12}/> Delete inquiry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="label-overline text-zinc-500">{label}</p>
      <p className="text-sm mt-1">{value || "—"}</p>
    </div>
  );
}

/* ============== APPLICATIONS ============== */
function ApplicationsPanel({ items, onRefresh }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [inviteId, setInviteId] = useState(null);
  const [declineId, setDeclineId] = useState(null);
  const selected = useMemo(() => items.find((a) => a.id === selectedId) || null, [items, selectedId]);
  const inviteTarget = useMemo(() => items.find((a) => a.id === inviteId) || null, [items, inviteId]);
  const declineTarget = useMemo(() => items.find((a) => a.id === declineId) || null, [items, declineId]);
  const filtered = useMemo(() => items.filter((a) => {
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return [a.full_name, a.email, a.city, a.country, a.instagram].some((f) => f?.toLowerCase().includes(s));
    }
    return true;
  }), [items, q, statusFilter]);

  return (
    <>
      <div className="mb-8">
        <p className="label-overline text-zinc-500">Open Casting</p>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-2">Applications.</h1>
        <p className="text-zinc-500 mt-1 text-sm">{filtered.length} of {items.length}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar value={q} onChange={setQ} placeholder="Search name, email, city…"/>
        <StatusFilter value={statusFilter} onChange={setStatusFilter} options={["pending", "contacted", "invited", "signed", "rejected", "archived"]}/>
      </div>

      {filtered.length === 0 ? <EmptyCard>No applications match your filters.</EmptyCard> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((a) => (
            <ApplicationCard
              key={a.id}
              a={a}
              onOpen={() => setSelectedId(a.id)}
              onInvite={() => setInviteId(a.id)}
              onDecline={() => setDeclineId(a.id)}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}

      <ApplicationDialog
        application={selected}
        open={!!selected}
        onClose={() => setSelectedId(null)}
        onInvite={() => setInviteId(selectedId)}
        onDecline={() => setDeclineId(selectedId)}
        onRefresh={() => { onRefresh(); }}
      />
      {inviteTarget && (
        <OutreachDialog
          mode="invite"
          application={inviteTarget}
          onClose={() => setInviteId(null)}
          onSent={() => onRefresh()}
        />
      )}
      {declineTarget && (
        <OutreachDialog
          mode="decline"
          application={declineTarget}
          onClose={() => setDeclineId(null)}
          onSent={() => onRefresh()}
        />
      )}
    </>
  );
}

function InviteButton({ a, onInvite, primary = false, testId }) {
  const done = ["signed", "rejected", "archived"].includes(a.status);
  if (done) return null;
  const again = !!a.invited_at;
  const cls = primary
    ? "btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs"
    : "label-overline text-xs text-indigo-800 hover:text-indigo-950 inline-flex items-center gap-1";
  return (
    <button type="button" data-testid={testId} onClick={onInvite} className={cls}>
      <MessageCircle size={12}/> {again ? "Invite again" : primary ? "Accept & invite to meet" : "Accept & invite"}
    </button>
  );
}

function ApplicationCard({ a, onOpen, onInvite, onDecline, onRefresh }) {
  const photos = a.photos || [];
  const cover = photos[0];
  const stop = (e) => e.stopPropagation();
  return (
    <div
      data-testid={`application-card-${a.id}`}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      className="border border-black/10 bg-white text-left hover:border-black hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] transition-all cursor-pointer focus:outline-none focus:border-black flex flex-col"
    >
      <div className="relative aspect-[5/4] bg-[#F5F5F5] overflow-hidden">
        {cover ? (
          <img src={imgSrc(cover)} alt={a.full_name} className="w-full h-full object-cover"/>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-400">
            <ImageIcon size={32}/>
          </div>
        )}
        <div className="absolute top-2 right-2"><StatusBadge status={a.status}/></div>
        {photos.length > 1 && (
          <span className="absolute bottom-2 right-2 bg-black/75 text-white text-[10px] px-2 py-0.5 uppercase tracking-wider">
            {photos.length} photos
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { stop(e); onOpen(); }}
          className="absolute bottom-2 left-2 bg-white/95 text-black text-[10px] uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 hover:bg-white"
        >
          <ZoomIn size={11}/> View details
        </button>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <p className="font-serif text-xl leading-tight">{a.full_name}</p>
          <span className="text-[10px] text-zinc-400 uppercase tracking-wider whitespace-nowrap">{relTime(a.created_at)}</span>
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          {a.gender} · {a.age}y{a.height_cm ? ` · ${a.height_cm} cm` : ""}{a.city ? ` · ${a.city}, ${a.country}` : ""}
        </p>
        {a.instagram && (
          <p className="text-xs text-zinc-600 mt-1.5 inline-flex items-center gap-1.5">
            <Instagram size={11}/> {a.instagram}
          </p>
        )}
        {a.invited_at && (
          <p data-testid={`application-invited-chip-${a.id}`} className="text-[10px] uppercase tracking-wider text-indigo-800 mt-2 inline-flex items-center gap-1">
            <MessageCircle size={10}/> Invited {relTime(a.invited_at)}{a.invite_count > 1 ? ` · ${a.invite_count}×` : ""}
          </p>
        )}
        {a.declined_at && (
          <p data-testid={`application-declined-chip-${a.id}`} className="text-[10px] uppercase tracking-wider text-zinc-500 mt-2 inline-flex items-center gap-1">
            Declined {relTime(a.declined_at)}{a.decline_emailed ? " · emailed" : " · no email"}
          </p>
        )}
        <div className="mt-auto pt-3 border-t border-black/5 flex flex-wrap items-center gap-2" onClick={stop}>
          <InviteButton a={a} onInvite={onInvite} testId={`application-invite-btn-${a.id}`}/>
          <StatusActions resource="applications" id={a.id} status={a.status} onRefresh={onRefresh} onDecline={onDecline} testId={`application-decline-btn-${a.id}`}/>
          <button
            onClick={async () => {
              if (!window.confirm(`Delete application from ${a.full_name}?`)) return;
              await api.delete(`/admin/applications/${a.id}`);
              toast.success("Deleted"); onRefresh();
            }}
            className="ml-auto label-overline text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"
          >
            <Trash2 size={12}/> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function ApplicationDialog({ application, open, onClose, onInvite, onDecline, onRefresh }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [revealPending, setRevealPending] = useState(false);
  useEffect(() => { if (!open) setLightboxIndex(null); }, [open]);
  useEffect(() => { setRevealed(!!application?.contact_revealed); }, [application]);

  if (!application) return null;
  const a = application;
  const photos = a.photos || [];
  const igHandle = (a.instagram || "").replace(/^@/, "").trim();
  const igUrl = igHandle ? `https://instagram.com/${igHandle}` : null;
  const phoneDigits = (a.phone || "").replace(/\D/g, "");
  const waUrl = phoneDigits ? `https://wa.me/${phoneDigits}` : null;
  const copyRef = async () => {
    try { await navigator.clipboard.writeText(a.reference || ""); toast.success("Reference copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const toggleReveal = async () => {
    setRevealPending(true);
    try {
      const next = !revealed;
      await api.put(`/admin/applications/${a.id}/reveal-contact`, { revealed: next });
      setRevealed(next);
      toast.success(next ? "Contact info released to partners" : "Contact info hidden from partners");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setRevealPending(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          data-testid="application-dialog"
          className="bg-white border-black/15 rounded-none max-w-6xl w-[96vw] text-[#0A0A0A] max-h-[92vh] overflow-y-auto p-0"
        >
          <div className="p-6 lg:p-8 border-b border-black/10 sticky top-0 bg-white z-10">
            <DialogHeader className="space-y-2 text-left">
              <p className="label-overline text-zinc-500 inline-flex items-center gap-2">
                <UserPlus size={11}/> Application · {a.reference || "—"}
                {a.reference && (
                  <button
                    type="button"
                    onClick={copyRef}
                    data-testid="copy-reference-btn"
                    className="ml-1 inline-flex items-center gap-1 text-zinc-500 hover:text-black"
                    title="Copy reference"
                  >
                    <Copy size={10}/>
                  </button>
                )}
              </p>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <DialogTitle className="font-serif text-3xl md:text-5xl font-light tracking-tighter leading-none">
                  {a.full_name}
                </DialogTitle>
                <StatusBadge status={a.status}/>
              </div>
              <DialogDescription className="text-zinc-500 text-sm">
                Submitted {fmtDate(a.created_at)} · {relTime(a.created_at)}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 lg:p-8">
            {/* PHOTOS */}
            <div className="lg:col-span-7">
              <p className="label-overline text-zinc-500 mb-3">Submitted photos · {photos.length}</p>
              {photos.length === 0 ? (
                <div className="aspect-[4/3] bg-[#F5F5F5] flex items-center justify-center text-zinc-400">
                  <ImageIcon size={32}/>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`application-photo-${i}`}
                      onClick={() => setLightboxIndex(i)}
                      className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden group focus:outline-none focus:ring-2 focus:ring-black"
                    >
                      <img
                        src={imgSrc(p)}
                        alt={`${a.full_name} ${i + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                        <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity"/>
                      </div>
                      <span className="absolute top-1 left-1 bg-white/90 text-black text-[10px] font-mono px-1.5 py-0.5">{i + 1}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* DETAILS */}
            <div className="lg:col-span-5 space-y-6">
              {/* Vitals */}
              <div>
                <p className="label-overline text-zinc-500 mb-3">Vitals</p>
                <dl className="space-y-2 text-sm">
                  <DetailRow icon={<UserPlus size={13}/>} label="Division">{a.gender || "—"}</DetailRow>
                  <DetailRow icon={<Hash size={13}/>} label="Age">{a.age ? `${a.age} years` : "—"}</DetailRow>
                  <DetailRow icon={<Ruler size={13}/>} label="Height">{a.height_cm ? `${a.height_cm} cm` : "—"}</DetailRow>
                  <DetailRow icon={<MapPin size={13}/>} label="Location">
                    {[a.city, a.country].filter(Boolean).join(", ") || "—"}
                  </DetailRow>
                </dl>
              </div>

              {/* Contact */}
              <div>
                <p className="label-overline text-zinc-500 mb-3">Contact</p>
                <dl className="space-y-2 text-sm">
                  <DetailRow icon={<Mail size={13}/>} label="Email">
                    <a
                      href={`mailto:${a.email}?subject=Ege Models · Your application ${a.reference || ""}`}
                      className="link-underline break-all"
                      data-testid="application-email-link"
                    >
                      {a.email}
                    </a>
                  </DetailRow>
                  <DetailRow icon={<Phone size={13}/>} label="Phone">
                    {a.phone ? (
                      <span className="flex items-center gap-3 flex-wrap">
                        <a href={`tel:${a.phone}`} className="link-underline">{a.phone}</a>
                        {waUrl && (
                          <a
                            href={waUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="application-whatsapp-link"
                            className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 text-xs"
                          >
                            <MessageCircle size={11}/> WhatsApp
                          </a>
                        )}
                      </span>
                    ) : "—"}
                  </DetailRow>
                  <DetailRow icon={<Instagram size={13}/>} label="Instagram">
                    {igUrl ? (
                      <a
                        href={igUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="application-instagram-link"
                        className="link-underline inline-flex items-center gap-1"
                      >
                        @{igHandle} <ExternalLink size={11}/>
                      </a>
                    ) : "—"}
                  </DetailRow>
                </dl>
              </div>

              {/* Outreach */}
              {(a.invited_at || a.declined_at) && (
                <div data-testid="application-outreach-block" className="border border-indigo-200 bg-indigo-50/60 p-4 space-y-3">
                  {a.invited_at && (
                    <div>
                      <p className="label-overline text-indigo-900 mb-2 inline-flex items-center gap-1.5"><MessageCircle size={11}/> Invited to meet</p>
                      <p className="text-sm text-zinc-800">
                        WhatsApp-outreach email sent {fmtDateTime(a.invited_at)}{a.invited_by ? ` by ${a.invited_by}` : ""}{a.invite_count > 1 ? ` · ${a.invite_count}×` : ""}.
                      </p>
                      {a.invite_note && (
                        <p className="text-sm text-zinc-700 mt-2 whitespace-pre-wrap border-l-2 border-indigo-300 pl-3">{a.invite_note}</p>
                      )}
                    </div>
                  )}
                  {a.declined_at && (
                    <div data-testid="application-declined-info">
                      <p className="label-overline text-zinc-700 mb-2">Declined</p>
                      <p className="text-sm text-zinc-800">
                        {a.decline_emailed ? "\"Not right now\" email sent" : "Declined without email"} {fmtDateTime(a.declined_at)}{a.declined_by ? ` by ${a.declined_by}` : ""}.
                      </p>
                      {a.decline_note && (
                        <p className="text-sm text-zinc-700 mt-2 whitespace-pre-wrap border-l-2 border-zinc-300 pl-3">{a.decline_note}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Bio */}
              {a.biography && (
                <div>
                  <p className="label-overline text-zinc-500 mb-3">Biography</p>
                  <p className="text-sm text-zinc-800 leading-relaxed italic whitespace-pre-wrap">
                    "{a.biography}"
                  </p>
                </div>
              )}

              {/* Experience */}
              {a.experience && (
                <div>
                  <p className="label-overline text-zinc-500 mb-3">Modeling experience</p>
                  <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">{a.experience}</p>
                </div>
              )}
            </div>
          </div>

          {/* ACTIONS */}
          <div className="border-t border-black/10 bg-[#FAFAFA] p-6 lg:p-8 sticky bottom-0">
            <div className="flex flex-wrap items-center gap-4">
              <p className="label-overline text-zinc-500">Quick actions</p>
              <div className="flex flex-wrap items-center gap-3 ml-auto" data-testid="application-dialog-actions">
                <InviteButton a={a} onInvite={onInvite} primary testId="application-dialog-invite-btn"/>
                <StatusActions
                  resource="applications"
                  id={a.id}
                  status={a.status}
                  onRefresh={() => { onRefresh(); }}
                  onDecline={onDecline}
                  testId="application-dialog-decline-btn"
                />
                <button
                  type="button"
                  data-testid="reveal-contact-toggle"
                  onClick={toggleReveal}
                  disabled={revealPending}
                  className={`label-overline text-xs inline-flex items-center gap-1 px-2 py-1 border transition-colors ${
                    revealed
                      ? "bg-emerald-100 text-emerald-900 border-emerald-300 hover:bg-emerald-200"
                      : "bg-zinc-50 text-zinc-700 border-black/20 hover:bg-black/5"
                  }`}
                  title="Toggle whether partner agencies can see this applicant's email & phone"
                >
                  <Building2 size={12}/>
                  {revealed ? "Contact released" : "Release to partners"}
                </button>
                <a
                  href={`mailto:${a.email}?subject=Ege Models · Your application ${a.reference || ""}&body=Hello ${a.full_name.split(" ")[0] || a.full_name},%0D%0A%0D%0A`}
                  className="label-overline text-xs text-zinc-700 hover:text-black inline-flex items-center gap-1"
                >
                  <Mail size={12}/> Email
                </a>
                {waUrl && (
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="label-overline text-xs text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1"
                  >
                    <MessageCircle size={12}/> WhatsApp
                  </a>
                )}
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete application from ${a.full_name}?`)) return;
                    await api.delete(`/admin/applications/${a.id}`);
                    toast.success("Deleted");
                    onClose();
                    onRefresh();
                  }}
                  data-testid="application-delete-btn"
                  className="label-overline text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"
                >
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Lightbox
        photos={photos}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        caption={a.full_name}
      />
    </>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
      <dt className="text-zinc-500 inline-flex items-center gap-1.5 pt-0.5">
        {icon}
        <span className="label-overline text-[10px]">{label}</span>
      </dt>
      <dd className="text-zinc-900">{children}</dd>
    </div>
  );
}

function Lightbox({ photos, index, onClose, caption }) {
  const [i, setI] = useState(index);
  useEffect(() => { setI(index); }, [index]);
  const prev = () => setI((x) => (x === 0 ? photos.length - 1 : x - 1));
  const next = () => setI((x) => (x === photos.length - 1 ? 0 : x + 1));
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index]); // eslint-disable-line

  if (index === null || i === null || !photos[i]) return null;
  return createPortal(
    <div
      data-testid="application-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-in fade-in duration-200"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="Close"
      >
        <X size={26}/>
      </button>
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            className="absolute left-4 lg:left-8 text-white/70 hover:text-white p-2 bg-black/50 rounded-full"
            aria-label="Previous"
          >
            <ChevronLeft size={28}/>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            className="absolute right-4 lg:right-8 text-white/70 hover:text-white p-2 bg-black/50 rounded-full"
            aria-label="Next"
          >
            <ChevronRight size={28}/>
          </button>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()} className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-3">
        <img
          src={imgSrc(photos[i])}
          alt={caption}
          className="max-w-full max-h-[80vh] object-contain shadow-2xl"
        />
        <p className="text-white/70 text-xs uppercase tracking-wider font-mono">
          {caption} · {i + 1} / {photos.length}
        </p>
      </div>
    </div>,
    document.body
  );
}

/* ============== CONTACTS ============== */
function ContactsPanel({ items, onRefresh }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const filtered = useMemo(() => items.filter((c) => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return [c.name, c.email, c.subject, c.message].some((f) => f?.toLowerCase().includes(s));
    }
    return true;
  }), [items, q, statusFilter]);

  return (
    <>
      <div className="mb-8">
        <p className="label-overline text-zinc-500">Inbox</p>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-2">Messages.</h1>
        <p className="text-zinc-500 mt-1 text-sm">{filtered.length} of {items.length}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar value={q} onChange={setQ} placeholder="Search sender, subject, message…"/>
        <StatusFilter value={statusFilter} onChange={setStatusFilter} options={["new", "contacted", "archived"]}/>
      </div>

      {filtered.length === 0 ? <EmptyCard>No messages match your filters.</EmptyCard> : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <div key={c.id} className="border border-black/10 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-serif text-xl">{c.name}</p>
                    <StatusBadge status={c.status}/>
                  </div>
                  <p className="text-sm text-zinc-500 mt-1">
                    <a href={`mailto:${c.email}`} className="hover:text-black underline">{c.email}</a>
                    {c.subject && <> · {c.subject}</>}
                  </p>
                </div>
                <span className="text-xs text-zinc-500 whitespace-nowrap">{fmtDate(c.created_at)}</span>
              </div>
              <p className="text-sm text-zinc-700 mt-3 leading-relaxed border-l-2 border-black/20 pl-4">{c.message}</p>
              <div className="mt-4 flex items-center gap-2">
                <StatusActions resource="contacts" id={c.id} status={c.status} onRefresh={onRefresh}/>
                <a href={`mailto:${c.email}?subject=Re: ${c.subject || "Your message"}&body=Hello ${c.name},%0D%0A%0D%0A`} className="ml-auto label-overline text-xs text-zinc-700 hover:text-black inline-flex items-center gap-1">
                  <Mail size={12}/> Reply
                </a>
                <button onClick={async () => {
                  if (!window.confirm("Delete this message?")) return;
                  await api.delete(`/admin/contacts/${c.id}`);
                  toast.success("Deleted"); onRefresh();
                }} className="label-overline text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1">
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ============== Shared bits ============== */
function StatusActions({ resource, id, status, onRefresh, onDecline, testId }) {
  const update = async (newStatus) => {
    try {
      await api.put(`/admin/${resource}/${id}/status`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      onRefresh();
    } catch (e) { toast.error("Update failed"); }
  };
  const isInbox = resource === "contacts";
  const isApp = resource === "applications";
  return (
    <>
      {(status === "new" || status === "pending") && (
        <button onClick={() => update("contacted")} className="label-overline text-xs text-blue-800 hover:text-blue-900 inline-flex items-center gap-1">
          <CheckCircle2 size={12}/> Mark contacted
        </button>
      )}
      {!isInbox && status !== "signed" && (
        <button onClick={() => update("signed")} className="label-overline text-xs text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1">
          <CheckCircle2 size={12}/> {isApp ? "Sign" : "Confirmed"}
        </button>
      )}
      {!isInbox && status !== "rejected" && (
        <button
          data-testid={testId}
          onClick={() => (onDecline ? onDecline() : update("rejected"))}
          className="label-overline text-xs text-zinc-600 hover:text-zinc-800"
        >
          Decline
        </button>
      )}
      {status !== "archived" && (
        <button onClick={() => update("archived")} className="label-overline text-xs text-zinc-600 hover:text-black inline-flex items-center gap-1">
          <Archive size={12}/> Archive
        </button>
      )}
    </>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="relative flex-1 min-w-[220px] max-w-md">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
      <input
        data-testid="search-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-black/15 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-black"
      />
    </div>
  );
}

function StatusFilter({ value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-44 bg-white border-black/15 rounded-none"><SelectValue/></SelectTrigger>
      <SelectContent className="bg-white border-black/15 rounded-none">
        <SelectItem value="all">All statuses</SelectItem>
        {options.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function EmptyCard({ children }) {
  return <div className="border border-black/10 bg-white p-12 text-center text-zinc-500 label-overline">{children}</div>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 14) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

/* ============== PASSWORD CHANGE DIALOG ============== */
function PasswordDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-white border-black/15 rounded-none max-w-md text-[#0A0A0A]">
        <DialogHeader>
          <p className="label-overline text-zinc-500">Security</p>
          <DialogTitle className="font-serif text-2xl font-normal">Change password</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">Use at least 8 characters. Mix letters, numbers and symbols.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Current password">
            <Input data-testid="pwd-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="New password">
            <Input data-testid="pwd-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Confirm new password">
            <Input data-testid="pwd-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
            <button type="submit" data-testid="pwd-submit" disabled={submitting} className="btn-primary">
              {submitting && <Loader2 size={14} className="animate-spin"/>} Update
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============== MODEL DIALOG ============== */
const EMPTY_MODEL_FORM = {
  name: "", gender: "women", board: "main",
  location: "", bio: "", cover_image: "",
  gallery: [], polaroids: [], video_reel: "",
  placements: [],
  instagram: "", featured: false,
  height_cm: "", bust_cm: "", waist_cm: "", hips_cm: "",
  shoe_eu: "", hair: "", eyes: "",
  unavailable_dates: [],
};

function modelToForm(model) {
  return {
    name: model.name, gender: model.gender, board: model.board || "main",
    location: model.location || "",
    bio: model.bio || "", cover_image: model.cover_image || "",
    gallery: model.gallery || [],
    polaroids: model.polaroids || [],
    video_reel: model.video_reel || "",
    placements: model.placements || [],
    instagram: model.instagram || "", featured: !!model.featured,
    height_cm: model.measurements?.height_cm || "", bust_cm: model.measurements?.bust_cm || "",
    waist_cm: model.measurements?.waist_cm || "", hips_cm: model.measurements?.hips_cm || "",
    shoe_eu: model.measurements?.shoe_eu || "", hair: model.measurements?.hair || "", eyes: model.measurements?.eyes || "",
    unavailable_dates: model.unavailable_dates || [],
  };
}

function ModelDialog({ children, model, onSaved }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => (model ? modelToForm(model) : EMPTY_MODEL_FORM));

  // Reset the form whenever the dialog opens — guarantees a clean slate for
  // "New Talent" and reloads latest values when re-opening an "Edit" dialog.
  useEffect(() => {
    if (open) setForm(model ? modelToForm(model) : EMPTY_MODEL_FORM);
  }, [open, model]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addPlacement = () => set("placements", [...form.placements, { agency: "", city: "" }]);
  const updatePlacement = (i, key, value) =>
    set("placements", form.placements.map((p, j) => (j === i ? { ...p, [key]: value } : p)));
  const removePlacement = (i) =>
    set("placements", form.placements.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.cover_image) { toast.error("Name and cover image required"); return; }
    setSubmitting(true);
    const payload = {
      name: form.name, gender: form.gender, board: form.board,
      division: "model",
      location: form.location, bio: form.bio,
      cover_image: form.cover_image,
      gallery: form.gallery,
      polaroids: form.polaroids,
      video_reel: form.video_reel || null,
      placements: form.placements.filter((p) => p.agency?.trim()).map((p) => ({
        agency: p.agency.trim(),
        city: p.city?.trim() || null,
      })),
      instagram: form.instagram, featured: form.featured,
      unavailable_dates: form.unavailable_dates,
      measurements: {
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        bust_cm: form.bust_cm ? Number(form.bust_cm) : null,
        waist_cm: form.waist_cm ? Number(form.waist_cm) : null,
        hips_cm: form.hips_cm ? Number(form.hips_cm) : null,
        shoe_eu: form.shoe_eu ? Number(form.shoe_eu) : null,
        hair: form.hair || null,
        eyes: form.eyes || null,
      },
    };
    try {
      if (model) await api.put(`/admin/models/${model.id}`, payload);
      else await api.post("/admin/models", payload);
      toast.success(model ? "Updated" : "Added");
      setOpen(false); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-white border-black/15 rounded-none max-w-2xl text-[#0A0A0A] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <p className="label-overline text-zinc-500">{model ? "Edit" : "New"} Talent</p>
          <DialogTitle className="font-serif text-2xl font-normal">{model ? model.name : "Add a Talent"}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">Upload photos from your device — drag-and-drop or click the tiles. JPG / PNG / WEBP / HEIC, up to 60 MB each.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label="Name *"><Input data-testid="m-name" value={form.name} onChange={(e)=>set("name",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <Field label="Location"><Input value={form.location} onChange={(e)=>set("location",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <Field label="Instagram"><Input value={form.instagram} onChange={(e)=>set("instagram",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>

          <div className="col-span-2">
            <Label className="label-overline text-zinc-700">Cover image *</Label>
            <div className="mt-2"><CoverUploader value={form.cover_image} onChange={(v)=>set("cover_image",v)}/></div>
          </div>

          <div className="col-span-2">
            <Label className="label-overline text-zinc-700">Editorial gallery</Label>
            <p className="text-xs text-zinc-500 mt-1">Up to 50 photos — final editorial shots, test shoots, campaign work. Drop multiple at once; they upload sequentially.</p>
            <div className="mt-2"><GalleryUploader value={form.gallery} onChange={(v)=>set("gallery",v)} max={50} testIdPrefix="gallery"/></div>
          </div>

          <div className="col-span-2">
            <Label className="label-overline text-zinc-700">Polaroids — natural / unedited</Label>
            <p className="text-xs text-zinc-500 mt-1">Up to 8 photos — phone snaps, neutral light, no filters.</p>
            <div className="mt-2"><GalleryUploader value={form.polaroids} onChange={(v)=>set("polaroids",v)} max={8} testIdPrefix="polaroid"/></div>
          </div>

          <div className="col-span-2">
            <Label className="label-overline text-zinc-700">International placements</Label>
            <p className="text-xs text-zinc-500 mt-1">Partner agencies this talent is signed/placed with. Shown on the public profile.</p>
            <div className="mt-3 space-y-2">
              {form.placements.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input
                    data-testid={`placement-agency-${i}`}
                    value={p.agency}
                    onChange={(e)=>updatePlacement(i,"agency",e.target.value)}
                    placeholder="Agency · e.g. Elite Model Management"
                    className="col-span-6 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
                  />
                  <Input
                    data-testid={`placement-city-${i}`}
                    value={p.city || ""}
                    onChange={(e)=>updatePlacement(i,"city",e.target.value)}
                    placeholder="City · e.g. Paris"
                    className="col-span-5 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
                  />
                  <button
                    type="button"
                    data-testid={`placement-remove-${i}`}
                    onClick={()=>removePlacement(i)}
                    className="col-span-1 text-zinc-500 hover:text-red-700 flex items-center justify-center"
                    aria-label="Remove placement"
                  >
                    <X size={16}/>
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="placement-add"
                onClick={addPlacement}
                className="label-overline text-zinc-700 hover:text-black inline-flex items-center gap-1"
              >
                <Plus size={12}/> Add placement
              </button>
            </div>
          </div>

          <div className="col-span-2"><Field label="Video reel URL (10–15 sec mp4 — optional)"><Input value={form.video_reel} onChange={(e)=>set("video_reel",e.target.value)} placeholder="https://…/reel.mp4" className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field></div>
          <div className="col-span-2"><Field label="Bio"><Textarea rows={3} value={form.bio} onChange={(e)=>set("bio",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field></div>
          <Field label="Height (cm)"><Input type="number" value={form.height_cm} onChange={(e)=>set("height_cm",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <Field label="Bust (cm)"><Input type="number" value={form.bust_cm} onChange={(e)=>set("bust_cm",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <Field label="Waist (cm)"><Input type="number" value={form.waist_cm} onChange={(e)=>set("waist_cm",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <Field label="Hips (cm)"><Input type="number" value={form.hips_cm} onChange={(e)=>set("hips_cm",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <Field label="Shoe (EU)"><Input type="number" step="0.5" value={form.shoe_eu} onChange={(e)=>set("shoe_eu",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <Field label="Hair"><Input value={form.hair} onChange={(e)=>set("hair",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field>
          <div className="col-span-2"><Field label="Eyes"><Input value={form.eyes} onChange={(e)=>set("eyes",e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/></Field></div>
          <div className="col-span-2 flex items-center gap-3 mt-2">
            <input data-testid="m-featured" type="checkbox" checked={form.featured} onChange={(e)=>set("featured",e.target.checked)} className="accent-black w-4 h-4"/>
            <Label className="label-overline text-zinc-700">Featured on homepage</Label>
          </div>
          <div className="col-span-2 mt-2 border-t border-black/10 pt-4">
            <Label className="label-overline text-zinc-700 mb-3 block">Availability — mark booked / unavailable dates</Label>
            <AvailabilityEditor value={form.unavailable_dates} onChange={(v) => set("unavailable_dates", v)}/>
          </div>
          <div className="col-span-2 flex justify-end gap-3 mt-2">
            <button type="button" onClick={()=>setOpen(false)} className="btn-ghost">Cancel</button>
            <button type="submit" data-testid="m-submit" disabled={submitting} className="btn-primary">
              {submitting && <Loader2 size={14} className="animate-spin"/>} Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="label-overline text-zinc-700">{label}</Label><div className="mt-2">{children}</div></div>;
}

/* ============== IMAGE UPLOADERS ============== */
// Client-side preprocessing + retry lives in /app/frontend/src/lib/uploadImage.js
// — any phone photo is resized to <= 2400 px / JPEG q=0.88 in the browser
// BEFORE upload, so the payload is always small and the upload can't be
// dropped by intermediate proxies / flaky mobile networks.
async function uploadImageFile(file) {
  return uploadImage(file);
}

function imgSrc(url) {
  // Relative /api/uploads/... is served from the same origin via ingress; absolute URLs (https://…) pass through.
  if (!url) return "";
  if (url.startsWith("/api/uploads/")) return `${API.replace(/\/api$/, "")}${url}`;
  return url;
}

function CoverUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImageFile(file);
      onChange(url);
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally { setBusy(false); }
  };

  const drop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImageFile(file);
      onChange(url);
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      {value ? (
        <div className="relative w-full aspect-[3/4] max-w-[240px] bg-[#F5F5F5] border border-black/15 overflow-hidden group">
          <img src={imgSrc(value)} alt="cover" className="w-full h-full object-cover" data-testid="m-cover-preview"/>
          <button type="button" data-testid="m-cover-remove" onClick={() => onChange("")} className="absolute top-2 right-2 bg-black/80 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <X size={14}/>
          </button>
        </div>
      ) : (
        <label
          data-testid="m-cover-drop"
          onDragOver={(e) => e.preventDefault()}
          onDrop={drop}
          className="flex flex-col items-center justify-center gap-2 w-full max-w-[240px] aspect-[3/4] border border-dashed border-black/30 bg-[#FAFAFA] hover:border-black hover:bg-white cursor-pointer transition-colors text-zinc-600"
        >
          {busy ? <Loader2 size={20} className="animate-spin"/> : <Upload size={20}/>}
          <span className="label-overline">{busy ? "Uploading…" : "Drop or click"}</span>
          <span className="text-xs text-zinc-500">JPG · PNG · WEBP · HEIC — max 60 MB</span>
          <input data-testid="m-cover-input" type="file" accept="image/*" className="hidden" onChange={pick} disabled={busy}/>
        </label>
      )}
      <div className="mt-2 text-xs">
        <button type="button" onClick={() => setShowUrl((s) => !s)} className="text-zinc-500 hover:text-black link-underline">
          {showUrl ? "Hide URL field" : "Or paste an image URL"}
        </button>
        {showUrl && (
          <Input
            data-testid="m-cover"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
          />
        )}
      </div>
    </div>
  );
}

function GalleryUploader({ value, onChange, max = 12, testIdPrefix = "g" }) {
  const [busy, setBusy] = useState(false);

  const addFiles = async (files) => {
    const remaining = max - value.length;
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) { toast.error(`Maximum ${max} photos`); return; }
    setBusy(true);
    const next = [...value];
    for (const f of list) {
      try {
        const url = await uploadImageFile(f);
        next.push(url);
        onChange([...next]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || `Failed: ${f.name}`);
      }
    }
    setBusy(false);
  };

  const pick = (e) => { addFiles(e.target.files || []); e.target.value = ""; };
  const drop = (e) => { e.preventDefault(); addFiles(e.dataTransfer.files || []); };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((url, i) => (
          <div key={`${url}-${i}`} className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden group border border-black/10">
            <img src={imgSrc(url)} alt="" className="w-full h-full object-cover"/>
            <button
              type="button"
              data-testid={`${testIdPrefix}-remove-${i}`}
              onClick={() => remove(i)}
              className="absolute top-1 right-1 bg-black/80 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={12}/>
            </button>
            <span className="absolute top-1 left-1 bg-white/90 text-black text-[10px] px-1.5 py-0.5 font-mono">{i + 1}</span>
          </div>
        ))}
        {value.length < max && (
          <label
            data-testid={`${testIdPrefix}-drop`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={drop}
            className="aspect-[3/4] flex flex-col items-center justify-center gap-1 border border-dashed border-black/30 bg-[#FAFAFA] hover:border-black hover:bg-white cursor-pointer transition-colors text-zinc-600"
          >
            {busy ? <Loader2 size={18} className="animate-spin"/> : <Upload size={18}/>}
            <span className="text-[10px] uppercase tracking-wider">{busy ? "Uploading" : `+ ${max - value.length} more`}</span>
            <input data-testid={`${testIdPrefix}-input`} type="file" accept="image/*" multiple className="hidden" onChange={pick} disabled={busy}/>
          </label>
        )}
      </div>
    </div>
  );
}
